const GOOGLE_ANALYTICS_FOUR_ID = process.env.GOOGLE_ANALYTICS_FOUR_ID ?? '';

/** ----------------------------------------------------------------------------------
The script below is required to connect with GA4.(2022/10/12 just now)

<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=<GA4 Measurement ID>"></script>
↑ This is automatically inserted by Google Tag Maneger.

<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '<GA4 Measurement ID>');
</script>
↑ Insert only this script in the initialization on prod.

-------------------------------------------------------------------------------------- */
const initializeGA4 = () => {
  // @ts-expect-error window object extended
  if (!window.GA4_INITIALIZED) {
    // eslint-disable-next-line no-console
    console.log('GA4 init');
    if (GOOGLE_ANALYTICS_FOUR_ID === process.env.GOOGLE_ANALYTICS_FOUR_ID_DEV) {
      const script1 = document.createElement('script');
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_FOUR_ID}`;
      script1.async = true;
      document.body.appendChild(script1);
    }
    const script2 = document.createElement('script');
    script2.text = `window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${GOOGLE_ANALYTICS_FOUR_ID}',{'send_page_view': false});
	`;
    document.body.appendChild(script2);

    // @ts-expect-error window object extended
    window.GA4_INITIALIZED = true;
  }
};

/** @deprecated use trackingEvent from now on. delete after transition to GA4 is complete */
export const trackingEventForGA4 = (action = '', category = '', label = '', parameters = {}) => {
  if (!action || !category) return;
  if (!GOOGLE_ANALYTICS_FOUR_ID) return;
  initializeGA4();

  const eventParams = {
    ua_event_action: action,
    ua_event_category: category,
    ua_event_label: label,
    ...parameters,
  };

  // eslint-disable-next-line no-console
  console.log(
    `Logging event for category ${category} and action ${action}${
      label ? ` with label ${label}. it send to GA4` : ''
    }`,
    parameters
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  gtag('event', action, eventParams);
};

function trackingEvent(
  eventName: string,
  parameters: Record<string, string | number | boolean> = {}
) {
  if (!eventName) return;
  if (!GOOGLE_ANALYTICS_FOUR_ID) return;
  initializeGA4();

  // eslint-disable-next-line no-console
  console.log(`Logging event for eventName ${eventName} it send to GA4`, parameters);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  gtag('event', eventName, parameters);
}

export const GA4EventRequestCreation = {
  blogViewed: 'blogViewed',
  proProfileViewed: 'proProfileViewed',
  cd_requestResultViewed: 'cd_requestResultViewed',
  startNewRequestClicked: 'startNewRequestClicked',
  requestIntroViewed: 'requestIntroViewed',
  requestIntroClicked: 'requestIntroClicked',
  q1Viewed: 'q1Viewed',
  q2Viewed: 'q2Viewed',
  q3Viewed: 'q3Viewed',
  q4Viewed: 'q4Viewed',
  q5Viewed: 'q5Viewed',
  q6Viewed: 'q6Viewed',
  q7Viewed: 'q7Viewed',
  q8Viewed: 'q8Viewed',
  q9Viewed: 'q9Viewed',
  addressViewed: 'addressViewed',
  addressClicked: 'addressClicked',
  matchingAnimationViewed: 'matchingAnimationViewed',
  emailViewed: 'emailViewed',
  emailClicked: 'emailClicked',
  nameViewed: 'nameViewed',
  nameClicked: 'nameClicked',
  phoneNumberViewed: 'phoneNumberViewed',
  phoneNumberClicked: 'phoneNumberClicked',
  summaryViewed: 'summaryViewed',
  summaryClicked: 'summaryClicked',
  socialLoginClicked: 'socialLoginClicked',
  socialEmailVerifyViewed: 'socialEmailVerifyViewed',
  socialEmailVerifyClicked: 'socialEmailVerifyClicked',
  socialUserAuthViewed: 'socialUserAuthViewed',
  socialUserAuthPasswordLink: 'socialUserAuthPasswordLink',
  socialUserAuthClicked: 'socialUserAuthClicked',
  socialUserAuthSuccess: 'socialUserAuthSuccess',
  socialLinkageCreated: 'socialLinkageCreated',
  socialPhoneNumberViewed: 'socialPhoneNumberViewed',
  socialPhoneNumberClicked: 'socialPhoneNumberClicked',
  newRequestSubmitted: 'newRequestSubmitted',
  quitRequestClicked: 'quitRequestClicked',
  quitRequestContinued: 'quitRequestContinued',
  quitRequestConfirmed: 'quitRequestConfirmed',
  quitRequestReasonSent: 'quitRequestReasonSent',
  newRequestCreated: 'newRequestCreated',
} as const;

type GA4EventRequestCreation = typeof GA4EventRequestCreation[keyof typeof GA4EventRequestCreation];

export function trackingEventForRequestCreation(
  eventName: GA4EventRequestCreation | '',
  parameters: {
    C1?: string;
    C2?: string;
    existingUser?: boolean;
    jobTypeId?: string;
    socialType?: string;
    whereFrom?: string;
  } = {}
) {
  trackingEvent(eventName, { ...parameters, group_newRequest: 'newRequest' });
}

export function getRequestCreationEventFromStep(
  currentStep: number,
  stepName: string,
  trigger: 'viewed' | 'clicked'
): GA4EventRequestCreation | '' {
  if (stepName === 'requestIntro' && trigger === 'viewed') {
    return GA4EventRequestCreation.requestIntroViewed;
  }
  if (stepName === 'requestIntro' && trigger === 'clicked') {
    return GA4EventRequestCreation.requestIntroClicked;
  }
  if (stepName === 'address' && trigger === 'viewed') {
    return GA4EventRequestCreation.addressViewed;
  }
  if (stepName === 'address' && trigger === 'clicked') {
    return GA4EventRequestCreation.addressClicked;
  }
  if (stepName === 'matchingAnimation' && trigger === 'viewed') {
    return GA4EventRequestCreation.matchingAnimationViewed;
  }
  if (stepName === 'email' && trigger === 'viewed') {
    return GA4EventRequestCreation.emailViewed;
  }
  if (stepName === 'email' && trigger === 'clicked') {
    return GA4EventRequestCreation.emailClicked;
  }
  if (stepName === 'name' && trigger === 'viewed') {
    return GA4EventRequestCreation.nameViewed;
  }
  if (stepName === 'name' && trigger === 'clicked') {
    return GA4EventRequestCreation.nameClicked;
  }
  if (stepName === 'phoneNumber' && trigger === 'viewed') {
    return GA4EventRequestCreation.phoneNumberViewed;
  }
  if (stepName === 'phoneNumber' && trigger === 'clicked') {
    return GA4EventRequestCreation.phoneNumberClicked;
  }
  if (stepName === 'summary' && trigger === 'viewed') {
    return GA4EventRequestCreation.summaryViewed;
  }
  if (stepName === 'summary' && trigger === 'clicked') {
    return GA4EventRequestCreation.summaryClicked;
  }
  if (trigger === 'viewed') {
    switch (currentStep) {
      case 1:
        return GA4EventRequestCreation.q1Viewed;
      case 2:
        return GA4EventRequestCreation.q2Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 3:
        return GA4EventRequestCreation.q3Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 4:
        return GA4EventRequestCreation.q4Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 5:
        return GA4EventRequestCreation.q5Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 6:
        return GA4EventRequestCreation.q6Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 7:
        return GA4EventRequestCreation.q7Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 8:
        return GA4EventRequestCreation.q8Viewed;
      // eslint-disable-next-line no-magic-numbers
      case 9:
        return GA4EventRequestCreation.q9Viewed;
    }
  }
  return '';
}

export const GA4EventDirectBooking = {
  db_lpViewed: 'db_lpViewed',
  db_startBookingClicked: 'db_startBookingClicked',
  db_locationIdViewed: 'db_locationIdViewed',
  db_locationIdClicked: 'db_locationIdClicked',
  db_coursesViewed: 'db_coursesViewed',
  db_coursesClicked: 'db_coursesClicked',
  db_listOptionsViewd: 'db_listOptionsViewd',
  db_listOptionsClicked: 'db_listOptionsClicked',
  db_dateTimeViewed: 'db_dateTimeViewed',
  db_dateTimeClicked: 'db_dateTimeClicked',
  db_pricingSummaryViewed: 'db_pricingSummaryViewed',
  db_pricingSummaryClicked: 'db_pricingSummaryClicked',
  db_personalInfoViewed: 'db_personalInfoViewed',
  db_personalInfoLoginClicked: 'db_personalInfoLoginClicked',
  db_loginViewed: 'db_loginViewed',
  db_loginForgetPasswordClicked: 'db_loginForgetPasswordClicked',
  db_loginClicked: 'db_loginClicked',
  db_personalInfoViewed_afterLogin: 'db_personalInfoViewed_afterLogin',
  db_personalInfoClicked: 'db_personalInfoClicked',
  db_paymentViewed: 'db_paymentViewed',
  db_paymentClicked: 'db_paymentClicked',
  db_bookingCompleted: 'db_bookingCompleted',
  db_bookingCompletedClicked: 'db_bookingCompletedClicked',
  db_dashboardFirstViewed: 'db_dashboardFirstViewed',
} as const;

type GA4EventDirectBooking = typeof GA4EventDirectBooking[keyof typeof GA4EventDirectBooking];

export function trackingEventForDirectBooking(
  eventName: GA4EventDirectBooking | '',
  parameters: {
    authenticated?: boolean;
    whereFrom?: string;
  } = {}
) {
  trackingEvent(eventName, { ...parameters, group_directBooking: 'directBooking' });
}

export function getDirectbookingEventFromStep(
  stepName: string,
  trigger: 'viewed' | 'clicked'
): GA4EventDirectBooking | '' {
  if (stepName === 'locationId' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_listOptionsViewd;
  }
  if (stepName === 'locationId' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_listOptionsClicked;
  }
  if (stepName === 'courses' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_coursesViewed;
  }
  if (stepName === 'courses' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_coursesClicked;
  }
  if (stepName === 'listOptions' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_listOptionsViewd;
  }
  if (stepName === 'listOptions' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_listOptionsClicked;
  }
  if (stepName === 'dateTime' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_dateTimeClicked;
  }
  if (stepName === 'dateTime' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_dateTimeClicked;
  }
  if (stepName === 'pricingSummary' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_pricingSummaryViewed;
  }
  if (stepName === 'pricingSummary' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_pricingSummaryClicked;
  }
  if (stepName === 'personalInfo' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_personalInfoViewed;
  }
  if (stepName === 'personalInfo' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_personalInfoClicked;
  }
  if (stepName === 'payment' && trigger === 'viewed') {
    return GA4EventDirectBooking.db_paymentViewed;
  }
  if (stepName === 'payment' && trigger === 'clicked') {
    return GA4EventDirectBooking.db_paymentClicked;
  }

  return '';
}

export const GA4EventProRegistration = {
  lp_topViewed: 'lp_topViewed',
  lp_c1Viewed: 'lp_c1Viewed',
  lp_c2Viewed: 'lp_c2Viewed',
  lp_c3Viewed: 'lp_c3Viewed',
  lp_verticalViewed: 'lp_verticalViewed',
  blogViewed: 'blogViewed',
  proProfileViewed: 'proProfileViewed',
  cd_myRequestsViewed: 'cd_myRequestsViewed',
  pro_goProRegistration: 'pro_goProRegistration',
  lp_proViewed: 'lp_proViewed',
  pro_startRegistrationClicked: 'pro_startRegistrationClicked',
  pro_socialLoginClicked: 'pro_socialLoginClicked',
  pro_socialEmailVerifyViewed: 'pro_socialEmailVerifyViewed',
  pro_socialEmailVerifyClicked: 'pro_socialEmailVerifyClicked',
  pro_socialUserAuthViewed: 'pro_socialUserAuthViewed',
  pro_socialUserAuthPasswordLink: 'pro_socialUserAuthPasswordLink',
  pro_socialUserAuthClicked: 'pro_socialUserAuthClicked',
  pro_socialUserAuthSuccess: 'pro_socialUserAuthSuccess',
  pro_selectJobTypeViewed: 'pro_selectJobTypeViewed',
  pro_selectJobTypeSearch: 'pro_selectJobTypeSearch',
  pro_selectJobTypeClicked: 'pro_selectJobTypeClicked',
  pro_relatedJobTypeViewed: 'pro_relatedJobTypeViewed',
  pro_relatedJobTypeAction: 'pro_relatedJobTypeAction',
  pro_relatedJobTypeClicked: 'pro_relatedJobTypeClicked',
  pro_nameViewed: 'pro_nameViewed',
  pro_nameClicked: 'pro_nameClicked',
  pro_addressViewed: 'pro_addressViewed',
  pro_addressClicked: 'pro_addressClicked',
  pro_emailViewed: 'pro_emailViewed',
  pro_emailClicked: 'pro_emailClicked',
  pro_phoneNumberViewed: 'pro_phoneNumberViewed',
  pro_phoneNumberClicked: 'pro_phoneNumberClicked',
  pro_passwordViewed: 'pro_passwordViewed',
  pro_referralViewed: 'pro_referralViewed',
  pro_referralClicked: 'pro_referralClicked',
  pro_basicInfoErrorViewed: 'pro_basicInfoErrorViewed',
  pro_basicInfoErrorLogin: 'pro_basicInfoErrorLogin',
  pro_basicInfoErrorCTA: 'pro_basicInfoErrorCTA',
  pro_quitRegistrationClicked: 'pro_quitRegistrationClicked',
  pro_quitRegistrationContinued: 'pro_quitRegistrationContinued',
  pro_quitRegistrationConfirmed: 'pro_quitRegistrationConfirmed',
  pro_registrationCompleted: 'pro_registrationCompleted',
  pro_registrationCompletedClicked: 'pro_registrationCompletedClicked',
  pro_quitRegistrationDocumentsClicked: 'pro_quitRegistrationDocumentsClicked',
  pro_businessInfoViewed: 'pro_businessInfoViewed',
  pro_businessInfoClicked: 'pro_businessInfoClicked',
  pro_avatarUploadViewed: 'pro_avatarUploadViewed',
  pro_avatarUploadClicked: 'pro_avatarUploadClicked',
  pro_avatarCroppingClicked: 'pro_avatarCroppingClicked',
  pro_avatarChangeClicked: 'pro_avatarChangeClicked',
  pro_avatarCompleteClicked: 'pro_avatarCompleteClicked',
  pro_profileCompleteViewed: 'pro_profileCompleteViewed',
  pro_profileCompleteClicked: 'pro_profileCompleteClicked',
} as const;

type GA4EventProRegistration = typeof GA4EventProRegistration[keyof typeof GA4EventProRegistration];

export function trackingEventForProRegistration(
  eventName: GA4EventProRegistration,
  parameters: {
    C1?: string;
    C2?: string;
    existingUser?: boolean;
    jobTypeId?: string;
    proSignUpTrees?: string;
    socialType?: string;
    whereFrom?: string;
  } = {}
) {
  trackingEvent(eventName, { ...parameters, group_proSignUp: 'proSignUp' });
}

export const GA4EventWebVital = {
  webVital_FCP: 'webVital_FCP',
  webVital_LCP: 'webVital_LCP',
  webVital_CLS: 'webVital_CLS',
  webVital_FID: 'webVital_FID',
  webVital_TTFB: 'webVital_TTFB',
} as const;

type GA4EventWebVital = typeof GA4EventWebVital[keyof typeof GA4EventWebVital];

export function trackingEventForWebVital(
  eventName: GA4EventWebVital,
  parameters: {
    effectiveType?: string;
    traceId?: string;
    value?: number;
  } = {}
) {
  trackingEvent(eventName, parameters);
}
